import { FC, useState } from "react"
import { Plus, Trash2 } from "lucide-react"

interface Props {
  template: string
  onChange: (template: string) => void
  dataType: "lead" | "subscription" | "pending_subscription"
}

const FlatTemplateBuilder: FC<Props> = ({ template, onChange, dataType }) => {
  const [newFieldName, setNewFieldName] = useState("")

  // Parse template to key-value pairs
  const parseTemplate = (): Record<string, string> => {
    if (!template || typeof template !== "string" || template.trim() === "") {
      return {}
    }
    try {
      return JSON.parse(template)
    } catch {
      return {}
    }
  }

  const templateObject = parseTemplate()

  // Common template variable suggestions
  const getVariableSuggestions = (): string[] => {
    const common = [
      "{{item.email}}",
      "{{item.phone}}",
      "{{lo.full_name}}",
      "{{lo.nmls}}",
      "{{lo.email}}",
      "{{brand.title}}",
      "{{brand.nmls}}"
    ]

    if (dataType === "lead") {
      return [
        ...common,
        "{{item.details.first_name}}",
        "{{item.details.last_name}}",
        "{{item.details.loan_amount}}",
        "{{item.details.credit_score}}",
        "{{@transform(item.details.loan_purpose)}}",
        "{{@transform(item.details.property_type)}}",
        "{{@transform(item.details.residency_type)}}"
      ]
    } else {
      return [
        ...common,
        "{{item.first_name}}",
        "{{item.last_name}}",
        "{{item.quote_info.loan_amount}}",
        "{{item.quote_info.credit_score}}",
        "{{@transform(item.quote_info.loan_purpose)}}",
        "{{@transform(item.quote_info.property_type)}}"
      ]
    }
  }

  const addField = () => {
    if (newFieldName && !(newFieldName in templateObject)) {
      const updated = { ...templateObject, [newFieldName]: "" }
      onChange(JSON.stringify(updated, null, 2))
      setNewFieldName("")
    }
  }

  const updateFieldName = (oldName: string, newName: string) => {
    const updated = { ...templateObject }
    const value = updated[oldName]
    delete updated[oldName]
    if (newName) {
      updated[newName] = value
    }
    onChange(JSON.stringify(updated, null, 2))
  }

  const updateFieldValue = (fieldName: string, value: string) => {
    const updated = { ...templateObject, [fieldName]: value }
    onChange(JSON.stringify(updated, null, 2))
  }

  const removeField = (fieldName: string) => {
    const updated = { ...templateObject }
    delete updated[fieldName]
    onChange(JSON.stringify(updated, null, 2))
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
        <div className="col-span-2">Field Name</div>
        <div className="col-span-3">Template Value</div>
        <div></div>
      </div>

      {Object.entries(templateObject).map(([fieldName, fieldValue]) => (
        <div
          key={fieldName}
          className="border border-gray-200 rounded-md p-3 bg-gray-50"
        >
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-2">
              <input
                type="text"
                value={fieldName}
                onChange={e => updateFieldName(fieldName, e.target.value)}
                placeholder="e.g., first_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
              />
            </div>
            <div className="col-span-3">
              <input
                type="text"
                value={fieldValue as string}
                onChange={e => updateFieldValue(fieldName, e.target.value)}
                placeholder="e.g., {{item.email}} or 400000 or {{@transform(item.details.loan_purpose)}}"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                list={`suggestions-${fieldName}`}
              />
              <datalist id={`suggestions-${fieldName}`}>
                {getVariableSuggestions().map(variable => (
                  <option key={variable} value={variable} />
                ))}
              </datalist>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => removeField(fieldName)}
                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                title="Remove field"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={newFieldName}
          onChange={e => setNewFieldName(e.target.value)}
          placeholder="Field name (e.g., first_name, loan_amount)"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1"
        />
        <button
          type="button"
          onClick={addField}
          disabled={!newFieldName}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center"
        >
          <Plus size={16} className="mr-1" />
          Add Field
        </button>
      </div>
    </div>
  )
}

export default FlatTemplateBuilder
